import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import { useQuery } from 'services/api/hooks';
import {
  campaignAutomationTasksSaved,
  createAutomationTask,
  deleteAutomationTask,
  getAutomationTasksByCampaignId,
  updateAutomationTask
} from 'services/automation/api';
import { AutomationTask, AutomationTaskInput } from 'services/automation/types';
import { getInitialValues } from './utils/getInitialValues';
import { AutomationItem } from './components/AutomationItem';
import { FormValues } from './types';
import { Box, SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { AddSectionButton, BackButton } from 'components/Buttons';
import { Campaign, ClosingMethod, Question } from 'services/campaign/types';
import { CampaignLaunchButtons } from 'modules/campaign-creation/components/CampaignLaunchButtons';
import { getDefaultValue } from './utils/getDefaultValue';
import { getCampaign } from 'services/campaign/api';
import { ToastType, useToast } from 'services/toast';
import { useRecoilValue } from 'recoil';
import { organizationAtom } from 'services/organization/atom';
import { useState } from 'react';
import { useRequirementConditionsOptionsAvailable } from './hooks/useRequirementConditionsOptionsAvailable';
import { useActionOptionsAvailable } from './hooks/useActionOptionsAvailable';
import { getDocumentTypesFromQuestions } from './utils/getDocumentTypesFromQuestions';
import { validateAndGetAutomationTasks } from './utils/validateAndGetAutomationTasks';
import { useTracking } from 'services/tracking/hooks';
import { TrackingEventName } from 'services/tracking/constants';
import {
  layoutContainerPaddingBottom,
  layoutContainerPaddingTop,
  layoutContainerPaddingX
} from 'services/navigation/components/PrivateRoute';

const containerBase: SxProps = {
  pt: layoutContainerPaddingTop,
  pb: layoutContainerPaddingBottom,
  px: layoutContainerPaddingX
};

const containerWithCampaign: SxProps = {
  ...containerBase,
  p: 4
};

const containerWithoutCampaign: SxProps = {
  ...containerBase,
  p: 0
};

const buttonsContainer: SxProps = {
  display: 'flex',
  gap: 1,
  mt: 2
};

type Props = {
  closingMethod?: ClosingMethod;
  setAutomationTasksToCreate?: (automationTasks: AutomationTaskInput[]) => void;
  handleNext?: () => void;
  isSubmitting?: boolean;
  atsJobId?: string | null | undefined;
  atsListId?: string | null | undefined;
  questions?: Question[];
  showLaunchButtons?: boolean;
};

const validationSchema = Yup.array().of(
  Yup.object().shape({
    requirementConditions: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().required('Required')
      })
    ),
    action: Yup.object().shape({
      type: Yup.string().required('Required'),
      recipientEmailAddress: Yup.string().email('Invalid email').nullable()
    })
  })
);

// TODO: Refactor and simplify
export const CampaignAutomationPage: React.FC<Props> = ({
  closingMethod: closingMethodProp,
  setAutomationTasksToCreate,
  isSubmitting,
  handleNext,
  atsJobId: atsJobIdProp,
  atsListId: atsListIdProp,
  questions,
  showLaunchButtons
}) => {
  const { campaignId } = useParams();
  const { showToast } = useToast();
  const organization = useRecoilValue(organizationAtom);
  const { track } = useTracking();
  const [automationTasksToDelete, setAutomationTasksToDelete] = useState<
    {
      id: string;
    }[]
  >([]);

  const { data: campaign, isLoading } = useQuery<Campaign>(getCampaign, {
    id: campaignId
  });

  const handleSubmit = async (values: FormValues) => {
    try {
      const validation = validateAndGetAutomationTasks(values);

      if (!validation.isValid) {
        showToast({
          message: validation.error || 'Validation failed.',
          type: ToastType.Error
        });
        return;
      }

      if (setAutomationTasksToCreate) {
        setAutomationTasksToCreate(validation.tasks);
        handleNext?.();
        return;
      }

      if (!organization?.id) throw new Error('Organization not found');
      if (!campaignId) throw new Error('Campaign not found');

      await Promise.all(
        validation.tasks.map((automationTask) => {
          if (automationTask.id) {
            return updateAutomationTask({
              id: automationTask.id,
              requirementConditions: automationTask.requirementConditions,
              action: automationTask.action
            });
          }

          track(TrackingEventName.CampaignAutomationCreate, {
            type: automationTask.action.type
          });

          return createAutomationTask({
            requirementConditions: automationTask.requirementConditions,
            action: automationTask.action,
            campaignId,
            organizationId: organization.id
          });
        })
      );

      await Promise.all(
        automationTasksToDelete.map((automationTask) =>
          deleteAutomationTask({
            id: automationTask.id
          })
        )
      );

      await campaignAutomationTasksSaved({ campaignId });
      await refetch();

      setAutomationTasksToDelete([]);
      showToast({
        message: 'Automations saved successfully',
        type: ToastType.Success
      });
    } catch (e: any) {
      showToast({
        message: 'We could not save your automation',
        type: ToastType.Error
      });
    }
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: [],
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
    validationSchema
  });

  const { refetch } = useQuery<AutomationTask[]>(
    getAutomationTasksByCampaignId,
    {
      campaignId
    },
    {
      onFetchSuccess: (data) => {
        formik.setValues(getInitialValues(data));
      }
    }
  );

  const closingMethod = closingMethodProp || campaign?.closingMethod;
  const atsJobId = campaign?.atsJobId || atsJobIdProp;
  const atsListId = campaign?.atsListId || atsListIdProp;
  const documentTypesAvailableOptions = getDocumentTypesFromQuestions(
    questions || campaign?.questions || []
  );

  const { conversationStatusAvailableOptions } =
    useRequirementConditionsOptionsAvailable(formik.values, closingMethod);

  const { actionOptionsAvailable } = useActionOptionsAvailable({
    atsJobId,
    atsListId
  });

  if (!closingMethod) {
    if (!isLoading)
      showToast({
        message: 'Failed to load automations',
        type: ToastType.Error
      });
    return null;
  }

  const addAutomation = () => {
    formik.setValues([
      ...formik.values,
      getDefaultValue({
        conversationStatusAvailableOptions,
        actionOptionsAvailable
      })
    ]);
  };

  const removeAutomationItem = (index: number) => {
    const newValues = formik.values.filter((_, i) => i !== index);
    const automationToDelete = formik.values
      .filter((el, i) => i === index && el.id)
      .map((item) => ({ id: item.id })) as { id: string }[];

    setAutomationTasksToDelete([
      ...automationTasksToDelete,
      ...automationToDelete
    ]);

    formik.setValues(newValues);
  };

  const onConfirmClick = () => {
    formik.handleSubmit();
  };

  return (
    <Box sx={campaignId ? containerWithCampaign : containerWithoutCampaign}>
      {campaignId && (
        <Box mb={3}>
          <BackButton />
        </Box>
      )}
      <form onSubmit={formik.handleSubmit}>
        {formik.values.map((automationTask, index) => (
          <Box mb={2} key={automationTask.id || `automation-item-${index}`}>
            <AutomationItem
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore: //
              formik={formik}
              index={index}
              closingMethod={closingMethod}
              removeAutomationItem={() => removeAutomationItem(index)}
              conversationStatusAvailableOptions={
                conversationStatusAvailableOptions
              }
              actionOptionsAvailable={actionOptionsAvailable}
              documentTypesAvailableOptions={documentTypesAvailableOptions}
              atsJobId={atsJobId}
              atsListId={atsListId}
            />
          </Box>
        ))}

        <Box mt={2}>
          <AddSectionButton key="add-automation-task" onClick={addAutomation}>
            Add Automation
          </AddSectionButton>
        </Box>

        <Box sx={buttonsContainer}>
          {showLaunchButtons && (
            <CampaignLaunchButtons
              validateAndGetTasks={() =>
                validateAndGetAutomationTasks(formik.values)
              }
              isLoading={isSubmitting || formik.isSubmitting}
            />
          )}
          {!showLaunchButtons && (
            <>
              <Button
                variant="primary"
                disabled={isSubmitting || formik.isSubmitting}
                onClick={onConfirmClick}
              >
                Confirm automations
              </Button>

              {handleNext && (
                <Button
                  variant="tertiary"
                  onClick={handleNext}
                  disabled={isSubmitting || formik.isSubmitting}
                >
                  Setup later
                </Button>
              )}
            </>
          )}
        </Box>
      </form>
    </Box>
  );
};
